/**
 * A small, dependency-free DMARC (RFC 7489) policy parser.
 *
 * It turns a published policy string (e.g. `"v=DMARC1; p=quarantine; rua=mailto:dmarc@example.com; pct=100"`)
 * into a structured decomposition the DMARC tab renders: the requested policy (and its strength), the
 * subdomain policy, the aggregate/failure report addresses, the alignment + tuning tags, and a set of
 * human-readable warnings for common misconfigurations.
 *
 * Like {@link parseSpf} this is a *display* aid, not a validator/evaluator — it never performs DNS lookups
 * and is lenient about malformed input (it records a warning rather than throwing) so the tab can always
 * render *something* for whatever the zone actually publishes. DMARC is a `;`-separated list of `tag=value`
 * pairs (vs. SPF's space-separated mechanisms), but the arc — parse → decompose → build → publish — mirrors SPF.
 */

export type DmarcPolicy = 'none' | 'quarantine' | 'reject';

/** DKIM/SPF identifier alignment: `r` = relaxed (default), `s` = strict. */
export type DmarcAlignment = 'r' | 's';

/** Severity hint for UI coloring (maps onto PatternFly Label/Alert colors). */
export type DmarcSeverity = 'success' | 'info' | 'warning' | 'danger';

/** One `tag=value` pair from the record, with a plain-English meaning (for the decomposition table). */
export interface DmarcTag {
  /** The original token, verbatim (e.g. `"p=quarantine"`). */
  raw: string;
  /** The tag name, lower-cased (e.g. `"p"`). */
  name: string;
  /** The value as published (case preserved). */
  value: string;
  /** `true` for a recognized RFC 7489 tag. */
  known: boolean;
  /** Plain-English meaning of the tag. */
  description: string;
}

export interface DmarcPolicyInfo {
  policy: DmarcPolicy;
  /** Short label, e.g. `'Reject (enforced)'`. */
  label: string;
  description: string;
  severity: DmarcSeverity;
}

export interface ParsedDmarc {
  /** `true` when the string is a well-formed DMARC record (starts with `v=DMARC1`). */
  valid: boolean;
  /** The version token, e.g. `'DMARC1'`, or `null` when invalid. */
  version: string | null;
  /** The requested domain policy (`p`), or `null` when absent/unrecognized. */
  policy: DmarcPolicy | null;
  /** The explicit subdomain policy (`sp`), or `null` when absent (it then inherits `p`). */
  subdomainPolicy: DmarcPolicy | null;
  /** The `pct` value (0–100), or `null` when absent/unparseable. */
  pct: number | null;
  /** Aggregate-report URIs (`rua`), in order (may be empty). */
  rua: string[];
  /** Failure-report URIs (`ruf`), in order (may be empty). */
  ruf: string[];
  /** DKIM alignment (`adkim`), or `null` when absent (defaults to relaxed). */
  adkim: DmarcAlignment | null;
  /** SPF alignment (`aspf`), or `null` when absent (defaults to relaxed). */
  aspf: DmarcAlignment | null;
  /** Failure-reporting options (`fo`), verbatim, or `null` when absent. */
  fo: string | null;
  /** Report format (`rf`), or `null` when absent (defaults to `afrf`). */
  rf: string | null;
  /** Aggregate report interval in seconds (`ri`), or `null` when absent/unparseable. */
  ri: number | null;
  /** Every tag in document order (incl. `v` and any unknown tags). */
  tags: DmarcTag[];
  /** The effective final policy, derived from `p`. */
  policyInfo: DmarcPolicyInfo | null;
  /** Human-readable advisories about misconfigurations / weak settings. */
  warnings: string[];
  /** The input, trimmed. */
  raw: string;
}

const POLICIES: readonly string[] = ['none', 'quarantine', 'reject'];

/** Plain-English meaning of a policy verb (qualifier-independent). */
function describePolicy(value: string): string {
  switch (value.toLowerCase()) {
    case 'none':
      return 'take no action (monitor only)';
    case 'quarantine':
      return 'send failing mail to spam/quarantine';
    case 'reject':
      return 'reject failing mail outright';
    default:
      return `"${value}" (unrecognized policy)`;
  }
}

/** Plain-English meaning of an `fo` (failure-options) value — single tokens or `:`-joined combos. */
function describeFo(value: string): string {
  const parts = value
    .split(':')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  const labels: Record<string, string> = {
    '0': 'when all mechanisms fail (default)',
    '1': 'when any mechanism fails',
    d: 'on a DKIM failure',
    s: 'on an SPF failure',
  };
  const described = parts.map((p) => labels[p.toLowerCase()] ?? `"${p}"`);
  return described.length > 0 ? `report ${described.join(' or ')}` : value;
}

/** Map a policy verb to a final-policy description + severity (mirrors SPF's buildPolicy). */
function buildDmarcPolicy(policy: DmarcPolicy): DmarcPolicyInfo {
  switch (policy) {
    case 'reject':
      return {
        policy,
        label: 'Reject (enforced)',
        description: 'Receivers reject mail that fails DMARC — the strictest, recommended setting.',
        severity: 'success',
      };
    case 'quarantine':
      return {
        policy,
        label: 'Quarantine',
        description: 'Receivers send mail that fails DMARC to spam/quarantine — a common enforced setting.',
        severity: 'info',
      };
    case 'none':
    default:
      return {
        policy: 'none',
        label: 'None (monitoring only)',
        description:
          'Receivers take no action on failing mail — you only collect reports. Tighten to quarantine or reject once your legitimate senders pass.',
        severity: 'warning',
      };
  }
}

/** Describe one recognized/unknown tag in plain English. */
function describeTag(name: string, value: string): { known: boolean; description: string } {
  switch (name) {
    case 'v':
      return { known: true, description: 'DMARC version' };
    case 'p':
      return { known: true, description: `Domain policy — ${describePolicy(value)}` };
    case 'sp':
      return { known: true, description: `Subdomain policy — ${describePolicy(value)}` };
    case 'rua':
      return { known: true, description: 'Address(es) that receive aggregate (statistical) DMARC reports' };
    case 'ruf':
      return {
        known: true,
        description: 'Address(es) that receive failure (forensic) reports — these can contain message content',
      };
    case 'pct':
      return { known: true, description: `Percentage of mail the policy is applied to (${value}%)` };
    case 'adkim':
      return { known: true, description: `DKIM alignment — ${value.toLowerCase() === 's' ? 'strict' : 'relaxed'}` };
    case 'aspf':
      return { known: true, description: `SPF alignment — ${value.toLowerCase() === 's' ? 'strict' : 'relaxed'}` };
    case 'fo':
      return { known: true, description: `Failure-report options — ${describeFo(value)}` };
    case 'rf':
      return { known: true, description: `Failure-report format — ${value}` };
    case 'ri':
      return { known: true, description: `Aggregate report interval — ${value} seconds` };
    default:
      return { known: false, description: `Unrecognized tag "${name}" (ignored by receivers)` };
  }
}

/** Split a comma-separated URI list (`rua`/`ruf`) into trimmed, non-empty entries. */
function splitUriList(value: string): string[] {
  return value
    .split(',')
    .map((u) => u.trim())
    .filter((u) => u.length > 0);
}

/**
 * Parse a DMARC policy string into its structured decomposition. Returns `{ valid: false }` (with empty
 * collections) for anything that is not a DMARC record, so callers can render an "invalid record" notice.
 */
export function parseDmarc(input: string | null | undefined): ParsedDmarc {
  const raw = (input ?? '').trim();

  const empty: ParsedDmarc = {
    valid: false,
    version: null,
    policy: null,
    subdomainPolicy: null,
    pct: null,
    rua: [],
    ruf: [],
    adkim: null,
    aspf: null,
    fo: null,
    rf: null,
    ri: null,
    tags: [],
    policyInfo: null,
    warnings: [],
    raw,
  };

  if (!raw) return empty;

  // DMARC is a ';'-separated list of tag=value pairs. Empty segments (e.g. a trailing ';') are ignored.
  const segments = raw
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const firstTag = /^([a-zA-Z]+)\s*=\s*(.*)$/.exec(segments[0] ?? '');
  if (!firstTag || firstTag[1].toLowerCase() !== 'v' || firstTag[2].trim().toLowerCase() !== 'dmarc1') {
    return { ...empty, warnings: ['This is not a DMARC record (it must begin with "v=DMARC1").'] };
  }

  const tags: DmarcTag[] = [];
  const warnings: string[] = [];
  const seen = new Set<string>();

  let policy: DmarcPolicy | null = null;
  let subdomainPolicy: DmarcPolicy | null = null;
  let pct: number | null = null;
  let rua: string[] = [];
  let ruf: string[] = [];
  let adkim: DmarcAlignment | null = null;
  let aspf: DmarcAlignment | null = null;
  let fo: string | null = null;
  let rf: string | null = null;
  let ri: number | null = null;

  for (const segment of segments) {
    const match = /^([a-zA-Z]+)\s*=\s*(.*)$/.exec(segment);
    if (!match) {
      warnings.push(`Malformed term "${segment}" was ignored.`);
      continue;
    }
    const name = match[1].toLowerCase();
    const value = match[2].trim();
    const { known, description } = describeTag(name, value);
    tags.push({ raw: segment, name, value, known, description });

    // Duplicate tags: receivers use the first occurrence (RFC 7489 §6.4) — keep it, warn about the rest.
    if (seen.has(name)) {
      warnings.push(`Duplicate tag "${name}" — only the first occurrence is used.`);
      continue;
    }
    seen.add(name);

    switch (name) {
      case 'v':
        break; // already validated as the first tag
      case 'p':
        if (POLICIES.includes(value.toLowerCase())) {
          policy = value.toLowerCase() as DmarcPolicy;
        } else {
          warnings.push(`Unknown policy "${value}" in p= — receivers may ignore the record.`);
        }
        break;
      case 'sp':
        if (POLICIES.includes(value.toLowerCase())) {
          subdomainPolicy = value.toLowerCase() as DmarcPolicy;
        } else {
          warnings.push(`Unknown subdomain policy "${value}" in sp=.`);
        }
        break;
      case 'pct': {
        const n = Number(value);
        if (Number.isInteger(n) && n >= 0 && n <= 100) {
          pct = n;
        } else {
          warnings.push('pct must be an integer between 0 and 100.');
        }
        break;
      }
      case 'rua':
        rua = splitUriList(value);
        break;
      case 'ruf':
        ruf = splitUriList(value);
        break;
      case 'adkim':
        if (value.toLowerCase() === 'r' || value.toLowerCase() === 's') {
          adkim = value.toLowerCase() as DmarcAlignment;
        } else {
          warnings.push('adkim must be "r" (relaxed) or "s" (strict).');
        }
        break;
      case 'aspf':
        if (value.toLowerCase() === 'r' || value.toLowerCase() === 's') {
          aspf = value.toLowerCase() as DmarcAlignment;
        } else {
          warnings.push('aspf must be "r" (relaxed) or "s" (strict).');
        }
        break;
      case 'fo':
        fo = value;
        break;
      case 'rf':
        rf = value;
        break;
      case 'ri': {
        const n = Number(value);
        if (Number.isInteger(n) && n >= 0) {
          ri = n;
        } else {
          warnings.push('ri (report interval) must be a non-negative integer number of seconds.');
        }
        break;
      }
      default:
        warnings.push(`Unrecognized tag "${name}" — receivers ignore it.`);
    }
  }

  // RFC 7489 §6.3: a policy record without a `p` tag is ignored by receivers.
  if (policy === null && !warnings.some((w) => w.startsWith('Unknown policy'))) {
    warnings.push('No policy tag (p=) — without it receivers ignore this record entirely.');
  }

  // --- Advisories ---------------------------------------------------------
  if (pct !== null && pct < 100) {
    warnings.push(
      `pct=${pct} applies the policy to only ${pct}% of mail; the remainder is treated as if p=none.`,
    );
  }
  if (rua.length === 0) {
    warnings.push('No aggregate report address (rua=) — you will not receive the reports needed to monitor DMARC.');
  }
  if (ruf.length > 0) {
    warnings.push(
      'Failure reports (ruf=) can include message headers/content (PII) — ensure the destination is authorized to receive them.',
    );
  }
  if (fo !== null && ruf.length === 0) {
    warnings.push('fo= has no effect without a failure-report address (ruf=).');
  }
  if (policy !== null && subdomainPolicy !== null) {
    const rank: Record<DmarcPolicy, number> = { none: 0, quarantine: 1, reject: 2 };
    if (rank[subdomainPolicy] < rank[policy]) {
      warnings.push(
        `The subdomain policy (sp=${subdomainPolicy}) is weaker than the domain policy (p=${policy}) — subdomains are less protected.`,
      );
    }
  }

  return {
    valid: true,
    version: 'DMARC1',
    policy,
    subdomainPolicy,
    pct,
    rua,
    ruf,
    adkim,
    aspf,
    fo,
    rf,
    ri,
    tags,
    policyInfo: policy !== null ? buildDmarcPolicy(policy) : null,
    warnings,
    raw,
  };
}

// ─── Builder (wizard) helpers ─────────────────────────────────────────────
//
// The inverse of parseDmarc: turn editable builder state into a record string, and (for prefill) turn an
// existing record back into builder state. Used by the DMARC builder wizard. The builder is "comprehensive"
// — it exposes every standard RFC 7489 tag — but emits only the tags that differ from their RFC default, so
// the published record stays clean.

/** A subdomain policy choice; `'inherit'` means "omit sp" (subdomains follow the domain policy). */
export type DmarcSubdomainPolicy = 'inherit' | DmarcPolicy;

/** The single-token `fo` (failure-options) values the builder offers. */
export type DmarcFailureOption = '0' | '1' | 'd' | 's';

/** RFC 7489 defaults — a tag equal to its default is omitted from the serialized record. */
export const DMARC_DEFAULT_PCT = 100;
export const DMARC_DEFAULT_RI = 86400;
export const DMARC_DEFAULT_RF = 'afrf';
export const DMARC_DEFAULT_FO: DmarcFailureOption = '0';

export const DMARC_POLICIES: readonly DmarcPolicy[] = ['none', 'quarantine', 'reject'];
export const DMARC_FAILURE_OPTIONS: readonly DmarcFailureOption[] = ['0', '1', 'd', 's'];

/** State the builder is seeded with when editing an existing record (see {@link dmarcToBuilder}). */
export interface DmarcBuilderState {
  policy: DmarcPolicy;
  subdomainPolicy: DmarcSubdomainPolicy;
  /** Aggregate-report addresses, mailto: prefixes stripped for editing (re-added on serialize). */
  rua: string[];
  /** Failure-report addresses, mailto: prefixes stripped for editing. */
  ruf: string[];
  /** 0–100; {@link DMARC_DEFAULT_PCT} is the default. */
  pct: number;
  adkim: DmarcAlignment;
  aspf: DmarcAlignment;
  fo: DmarcFailureOption;
  /** Report interval in seconds; {@link DMARC_DEFAULT_RI} is the default. */
  ri: number;
  rf: string;
  /**
   * Tokens from the source record the builder can't represent (unknown tags, an unrepresentable
   * `fo` combo, etc.). Surfaced so the UI can warn that rebuilding would drop them.
   */
  unsupported: string[];
}

/** Strip a leading `mailto:` so the address shows bare in the builder input. */
export function stripMailto(uri: string): string {
  return /^mailto:/i.test(uri.trim()) ? uri.trim().slice('mailto:'.length) : uri.trim();
}

/**
 * Normalize a report address for serialization: a bare email gets a `mailto:` prefix; a non-`mailto:` URI
 * (`https:`…) is kept as-is; a `mailto:` URI is kept only when it carries a real address. Empty input, a
 * scheme-only `mailto:`, or anything that is not a usable DMARC URI yields `null` (the row is dropped) so a
 * malformed value can never reach the published record — the wizard validates inputs, but `dmarcToBuilder`
 * round-trips and any direct serialize call rely on this guard too.
 */
export function normalizeDmarcUri(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  // A mailto: URI must carry a real address — a scheme-only "mailto:" is not a valid report URI.
  if (/^mailto:/i.test(v)) {
    return /^mailto:[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(v) ? v : null;
  }
  // Any other URI scheme (https:, …) is kept as-is — it carries its own validation contract.
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return v;
  // A bare email address gets a mailto: prefix; anything else is not a usable DMARC URI — drop it.
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return `mailto:${v}`;
  return null;
}

/**
 * Assemble builder state into a full DMARC record string. Only `v` and `p` are always emitted; every other
 * tag is emitted only when it differs from its RFC 7489 default (or, for reports, when at least one address
 * is provided), so the published record stays minimal. Report rows whose value is empty are skipped.
 */
export function serializeDmarc(state: DmarcBuilderState): string {
  const tokens: string[] = ['v=DMARC1', `p=${state.policy}`];

  if (state.subdomainPolicy !== 'inherit') {
    tokens.push(`sp=${state.subdomainPolicy}`);
  }

  const rua = state.rua.map(normalizeDmarcUri).filter((u): u is string => u !== null);
  if (rua.length > 0) tokens.push(`rua=${rua.join(',')}`);

  const ruf = state.ruf.map(normalizeDmarcUri).filter((u): u is string => u !== null);
  if (ruf.length > 0) tokens.push(`ruf=${ruf.join(',')}`);

  if (state.adkim !== 'r') tokens.push(`adkim=${state.adkim}`);
  if (state.aspf !== 'r') tokens.push(`aspf=${state.aspf}`);
  if (state.pct !== DMARC_DEFAULT_PCT) tokens.push(`pct=${state.pct}`);
  if (state.fo !== DMARC_DEFAULT_FO) tokens.push(`fo=${state.fo}`);
  if (state.ri !== DMARC_DEFAULT_RI) tokens.push(`ri=${state.ri}`);
  if (state.rf.trim() && state.rf.trim().toLowerCase() !== DMARC_DEFAULT_RF) tokens.push(`rf=${state.rf.trim()}`);

  return tokens.join('; ');
}

/**
 * Decompose an existing DMARC record into builder state for prefill. Recognized tags become editable
 * fields; anything the builder can't represent (unknown tags, an `fo` combo we can't show in a single
 * select, an unrecognized `p`/`sp`) goes into `unsupported`. A blank/invalid input yields the safe
 * `p=none` monitoring default.
 */
export function dmarcToBuilder(value: string | null | undefined): DmarcBuilderState {
  const parsed = parseDmarc(value);
  const unsupported: string[] = [];

  const base: DmarcBuilderState = {
    policy: 'none',
    subdomainPolicy: 'inherit',
    rua: [],
    ruf: [],
    pct: DMARC_DEFAULT_PCT,
    adkim: 'r',
    aspf: 'r',
    fo: DMARC_DEFAULT_FO,
    ri: DMARC_DEFAULT_RI,
    rf: DMARC_DEFAULT_RF,
    unsupported,
  };

  if (!parsed.valid) {
    return base;
  }

  base.policy = parsed.policy ?? 'none';
  base.subdomainPolicy = parsed.subdomainPolicy ?? 'inherit';
  base.rua = parsed.rua.map(stripMailto);
  base.ruf = parsed.ruf.map(stripMailto);
  base.pct = parsed.pct ?? DMARC_DEFAULT_PCT;
  base.adkim = parsed.adkim ?? 'r';
  base.aspf = parsed.aspf ?? 'r';
  base.ri = parsed.ri ?? DMARC_DEFAULT_RI;
  base.rf = parsed.rf ?? DMARC_DEFAULT_RF;

  // fo can be a ':'-joined combo (e.g. "1:d:s") the single-select can't represent — keep only a lone token.
  if (parsed.fo !== null) {
    const lone = parsed.fo.trim().toLowerCase();
    if ((DMARC_FAILURE_OPTIONS as readonly string[]).includes(lone)) {
      base.fo = lone as DmarcFailureOption;
    } else {
      unsupported.push(`fo=${parsed.fo}`);
    }
  }

  // An unrecognized p/sp value can't be mapped to a builder choice — record it so the user is warned.
  for (const tag of parsed.tags) {
    if (!tag.known) {
      unsupported.push(tag.raw);
    } else if (tag.name === 'p' && parsed.policy === null) {
      unsupported.push(tag.raw);
    } else if (tag.name === 'sp' && parsed.subdomainPolicy === null) {
      unsupported.push(tag.raw);
    }
  }

  return base;
}
